import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ClientsService } from '../../core/services/clients.service';
import { MealRecordsService } from '../../core/services/meal-records.service';
import { Client } from '../../core/models/client.model';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatTableModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  styles: [
    `:host{display:block}
     .page-header{display:flex;flex-direction:column;gap:4px}
     .page-title{margin:0;font-weight:600;letter-spacing:.2px;color:#1F2937}
     .page-subtitle{margin:0;color:rgba(0,0,0,0.6);font-size:13px}
     .card{margin-top:12px;border:1px solid rgba(0,0,0,0.08);border-radius:8px;overflow:hidden}
     .card-body{padding:16px}
     .filters-row{display:flex;gap:12px;flex-wrap:wrap;align-items:flex-end}
     .filters-row .mat-form-field{min-width:220px}
     .generate-btn{align-self:flex-end}
     .actions{display:flex;gap:8px;align-items:center}
     table{width:100%}
     .totals td{font-weight:600;border-top:1px solid #D1D5DB;background:transparent}`
  ],
  template: `
    <div class="page-header">
      <h2 class="page-title">{{ 'reports.pageTitle' | translate }}</h2>
      <p class="page-subtitle">{{ 'reports.pageSubtitle' | translate }}</p>
    </div>

    <mat-card class="card">
      <div class="card-body" [formGroup]="form">
        <div style="margin-bottom:8px;font-weight:600;">{{ 'reports.filters' | translate }}</div>
        <div class="filters-row">
          <mat-form-field appearance="outline">
            <mat-label>{{ 'reports.month' | translate }} *</mat-label>
            <input matInput [matDatepicker]="monthPicker" formControlName="month" required readonly>
            <mat-datepicker-toggle matSuffix [for]="monthPicker"></mat-datepicker-toggle>
            <mat-datepicker #monthPicker startView="multi-year" (yearSelected)="onYearSelected($event)" (monthSelected)="onMonthSelected($event, monthPicker)"></mat-datepicker>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'reports.client' | translate }}</mat-label>
            <mat-select formControlName="clientId">
              <mat-option [value]="''">{{ 'common.all' | translate }}</mat-option>
              <mat-option *ngFor="let c of clients" [value]="c.id">{{ c.name }}</mat-option>
            </mat-select>
          </mat-form-field>
          <div class="actions">
            <button mat-raised-button color="primary" class="generate-btn" (click)="reload()" [disabled]="!form.value.month">
              <mat-icon>refresh</mat-icon>
              {{ 'reports.generate' | translate }}
            </button>
            <button mat-stroked-button color="primary" (click)="exportCsv()" [disabled]="!rows.length">
              <mat-icon>file_download</mat-icon>
              {{ 'reports.export' | translate }}
            </button>
          </div>
        </div>
      </div>
    </mat-card>

    <mat-card class="card">
      <div class="card-body" *ngIf="loading" style="display:flex; justify-content:center; padding:8px 0;">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
      <div class="card-body" *ngIf="!loading && !rows.length">{{ 'reports.noRecords' | translate }}</div>

      <table mat-table [dataSource]="rows" *ngIf="!loading && rows.length">
        <ng-container matColumnDef="client">
          <th mat-header-cell *matHeaderCellDef>{{ 'reports.client' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.clientName }}</td>
          <td mat-footer-cell *matFooterCellDef style="font-weight:600;">{{ 'reports.totalRow' | translate }}</td>
        </ng-container>
        <ng-container matColumnDef="breakfast">
          <th mat-header-cell *matHeaderCellDef>{{ 'reports.breakfast' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.breakfast }}</td>
          <td mat-footer-cell *matFooterCellDef style="font-weight:600;">{{ totals.breakfast }}</td>
        </ng-container>
        <ng-container matColumnDef="lunch">
          <th mat-header-cell *matHeaderCellDef>{{ 'reports.lunch' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.lunch }}</td>
          <td mat-footer-cell *matFooterCellDef style="font-weight:600;">{{ totals.lunch }}</td>
        </ng-container>
        <ng-container matColumnDef="dinner">
          <th mat-header-cell *matHeaderCellDef>{{ 'reports.dinner' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.dinner }}</td>
          <td mat-footer-cell *matFooterCellDef style="font-weight:600;">{{ totals.dinner }}</td>
        </ng-container>
        <ng-container matColumnDef="package">
          <th mat-header-cell *matHeaderCellDef>{{ 'reports.lunchPackage' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.package }}</td>
          <td mat-footer-cell *matFooterCellDef style="font-weight:600;">{{ totals.package }}</td>
        </ng-container>
        <ng-container matColumnDef="totalMeals">
          <th mat-header-cell *matHeaderCellDef>{{ 'reports.totalMeals' | translate }}</th>
          <td mat-cell *matCellDef="let r">{{ r.totalMeals }}</td>
          <td mat-footer-cell *matFooterCellDef style="font-weight:600;">{{ totals.totalMeals }}</td>
        </ng-container>
        <ng-container matColumnDef="amount">
          <th mat-header-cell *matHeaderCellDef>{{ 'reports.amount' | translate }} ({{ currency }})</th>
          <td mat-cell *matCellDef="let r">{{ r.amount | number:'1.2-2' }}</td>
          <td mat-footer-cell *matFooterCellDef style="font-weight:600;">{{ totals.amount | number:'1.2-2' }}</td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        <tr mat-footer-row class="totals" *matFooterRowDef="displayedColumns"></tr>
      </table>

      
    </mat-card>
  `
})
export class ReportsComponent implements OnInit {
  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    private recordsService: MealRecordsService,
    private t: TranslateService
  ) {}

  currency = environment.currency;
  clients: Client[] = [];
  rows: Array<{ clientId: string; clientName: string; breakfast: number; lunch: number; dinner: number; package: number; totalMeals: number; amount: number }> = [];
  displayedColumns = ['client', 'breakfast', 'lunch', 'dinner', 'package', 'totalMeals', 'amount'];
  grandTotal = 0;
  loading = false;
  totals = { breakfast: 0, lunch: 0, dinner: 0, package: 0, totalMeals: 0, amount: 0 };

  form = this.fb.group({
    month: [new Date()],
    clientId: ['']
  });

  ngOnInit(): void {
    this.clientsService.getAll().subscribe(list => {
      this.clients = list;
      this.reload();
    });
  }

  reload() {
    const v = this.form.value;
    const month = v.month as Date;
    if (!month) return;
    const from = new Date(month.getFullYear(), month.getMonth(), 1);
    const to = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const fromIso = new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())).toISOString();
    const toIso = new Date(Date.UTC(to.getFullYear(), to.getMonth(), to.getDate())).toISOString();

    const clientId = (v.clientId as string) || '';
    this.loading = true;
    if (clientId) {
      const client = this.clients.find(c => c.id === clientId);
      this.recordsService.report(clientId, fromIso, toIso).subscribe({
        next: res => {
          const row = this.buildRow(clientId, client?.name || '', res);
          this.rows = row.totalMeals > 0 || row.amount > 0 ? [row] : [];
          this.computeTotals();
          this.loading = false;
        },
        error: () => { this.rows = []; this.grandTotal = 0; this.loading = false; }
      });
    } else {
      const clients = this.clients;
      const rows: typeof this.rows = [];
      let total = 0;
      let idx = 0;
      const next = () => {
        if (idx >= clients.length) {
          this.rows = rows.filter(r => r.totalMeals > 0 || r.amount > 0);
          this.computeTotals();
          this.loading = false;
          return;
        }
        const c = clients[idx++];
        this.recordsService.report(c.id, fromIso, toIso).subscribe({
          next: res => {
            const r = this.buildRow(c.id, c.name, res);
            rows.push(r);
            total += r.amount;
            next();
          },
          error: () => next()
        });
      };
      next();
    }
  }

  private buildRow(clientId: string, clientName: string, res: any) {
    const pick = (label: string) => (res.items || []).find((i: any) => i.mealType === label || i.mealType === this.mapType(label)) || { quantity: 0, total: 0 };
    const breakfast = pick('Breakfast').quantity || 0;
    const lunch = pick('Lunch').quantity || 0;
    const dinner = pick('Dinner').quantity || 0;
    const pkg = pick('LunchPackage').quantity || 0;
    const totalMeals = breakfast + lunch + dinner + pkg;
    const amount = Number(res.grandTotal || 0);
    return { clientId, clientName, breakfast, lunch, dinner, package: pkg, totalMeals, amount };
  }

  private mapType(type: any): number | string {
    if (type === 'Breakfast') return 0;
    if (type === 'Lunch') return 1;
    if (type === 'Dinner') return 2;
    if (type === 'LunchPackage') return 3;
    return type;
  }

  onYearSelected(d: Date) {
    const month = this.form.value.month as Date || new Date();
    const updated = new Date(month);
    updated.setFullYear(d.getFullYear());
    this.form.patchValue({ month: updated });
  }

  onMonthSelected(d: Date, datepicker: any) {
    const updated = new Date(d.getFullYear(), d.getMonth(), 1);
    this.form.patchValue({ month: updated });
    datepicker.close();
  }

  private computeTotals() {
    const t = { breakfast: 0, lunch: 0, dinner: 0, package: 0, totalMeals: 0, amount: 0 };
    for (const r of this.rows) {
      t.breakfast += r.breakfast;
      t.lunch += r.lunch;
      t.dinner += r.dinner;
      t.package += r.package;
      t.totalMeals += r.totalMeals;
      t.amount += r.amount;
    }
    this.totals = t;
    this.grandTotal = t.amount;
  }

  exportCsv() {
    if (!this.rows.length) return;
    const headers = ['Client','Breakfast','Lunch','Dinner','LunchPackage','Total','Amount'];
    const lines = [headers.join(',')];
    for (const r of this.rows) {
      const row = [
        escapeCsv(r.clientName),
        r.breakfast,
        r.lunch,
        r.dinner,
        r.package,
        r.totalMeals,
        r.amount
      ];
      lines.push(row.join(','));
    }
    lines.push(['TOTAL', this.totals.breakfast, this.totals.lunch, this.totals.dinner, this.totals.package, this.totals.totalMeals, this.totals.amount].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const v = this.form.value;
    const month = v.month as Date;
    const label = month ? `${month.getFullYear()}-${String(month.getMonth()+1).padStart(2,'0')}` : 'report';
    a.download = `report-${label}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    function escapeCsv(value: any) {
      const s = String(value ?? '');
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    }
  }
}
